import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ChevronLeft, ChevronRight, HeartPulse, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useSyncState } from "@/hooks/use-itsm-config";
import {
  useDeviceSignal,
  useTenantIntegrations,
  type TenantIntegration,
} from "@/hooks/use-integration-health";
import {
  DEVICE_CATEGORIES,
  devicesVerdict,
  itsmVerdict,
  type HealthLevel,
  type HealthVerdict,
} from "@/lib/solution-engineer/integration-health";
import { PROVIDER_FAMILY } from "@/lib/types/itsm-config";
import { cn } from "@/lib/utils";
import { ToolCard } from "./tool-card";

const LEVEL_STYLES: Record<HealthLevel, string> = {
  healthy: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  warning: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
  error: "bg-red-500/15 text-red-700 dark:text-red-400",
  inactive: "bg-muted text-muted-foreground",
  unknown: "bg-muted text-muted-foreground",
};

const PAGE_SIZE = 5;

const ago = (iso: string) =>
  formatDistanceToNow(new Date(iso), { addSuffix: true });

/** ITSM providers with an inbound ticket sync, as the ITSM Config page decides it. */
const hasItsmSync = (provider: string) => {
  // eslint-disable-next-line security/detect-object-injection
  const family = PROVIDER_FAMILY[provider] as string | undefined;
  return family !== undefined && family !== "pm";
};

function VerdictBadge({ verdict }: { verdict: HealthVerdict }) {
  return (
    <Badge variant="secondary" className={LEVEL_STYLES[verdict.level]}>
      {verdict.label}
    </Badge>
  );
}

function ItsmSignal({
  tenantId,
  integration,
}: {
  tenantId: string;
  integration: TenantIntegration;
}) {
  const {
    data: state,
    isLoading,
    isError,
  } = useSyncState(tenantId, integration.id);
  if (isLoading)
    return <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />;
  if (isError || state === undefined) {
    return (
      <VerdictBadge verdict={{ level: "unknown", label: "Couldn't load" }} />
    );
  }
  const verdict = itsmVerdict(state);
  return (
    <div className="space-y-1">
      <VerdictBadge verdict={verdict} />
      <p className="text-muted-foreground text-xs">
        {state?.last_sync_at
          ? `Last ticket sync ${ago(state.last_sync_at)}`
          : "No ticket sync yet"}
      </p>
      {verdict.detail && (
        <p
          className="text-destructive line-clamp-2 text-xs"
          title={verdict.detail}
        >
          {verdict.detail}
        </p>
      )}
    </div>
  );
}

function DeviceSignalCell({
  tenantId,
  integration,
}: {
  tenantId: string;
  integration: TenantIntegration;
}) {
  const {
    data: signal,
    isLoading,
    isError,
  } = useDeviceSignal(tenantId, integration.id);
  if (isLoading)
    return <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />;
  if (isError || !signal) {
    return (
      <VerdictBadge verdict={{ level: "unknown", label: "Couldn't load" }} />
    );
  }
  return (
    <div className="space-y-1">
      <VerdictBadge verdict={devicesVerdict(signal)} />
      <p className="text-muted-foreground text-xs">
        {signal.total} devices
        {signal.latestCheckIn &&
          ` · latest check-in ${ago(signal.latestCheckIn)}`}
      </p>
    </div>
  );
}

function HealthCell({
  tenantId,
  integration,
}: {
  tenantId: string;
  integration: TenantIntegration;
}) {
  if (!integration.enabled) {
    return <VerdictBadge verdict={{ level: "inactive", label: "Disabled" }} />;
  }
  if (hasItsmSync(integration.provider)) {
    return <ItsmSignal tenantId={tenantId} integration={integration} />;
  }
  if (DEVICE_CATEGORIES.has(integration.category)) {
    return <DeviceSignalCell tenantId={tenantId} integration={integration} />;
  }
  return (
    <span className="text-muted-foreground text-xs">
      No health signal from here
    </span>
  );
}

/** Each row loads its own signal, so order by what is known up front: enabled rows with a signal first, disabled last. */
function rowRank(integration: TenantIntegration): number {
  if (!integration.enabled) return 2;
  if (
    hasItsmSync(integration.provider) ||
    DEVICE_CATEGORIES.has(integration.category)
  ) {
    return 0;
  }
  return 1;
}

export function IntegrationHealthTool({ tenantId }: { tenantId: string }) {
  const {
    data: integrations,
    isLoading,
    isError,
  } = useTenantIntegrations(tenantId);
  const [page, setPage] = useState(0);

  const rows = [...(integrations ?? [])].sort(
    (a, b) => rowRank(a) - rowRank(b) || a.category.localeCompare(b.category),
  );
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <ToolCard
      icon={HeartPulse}
      title="Integration health"
      description="Every integration the tenant has connected, with ITSM ticket-sync state and MDM/EDR device freshness. Read-only; no credentials are loaded."
    >
      {isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading integrations...
        </div>
      ) : isError ? (
        <p className="text-destructive text-sm">
          Failed to load the tenant&apos;s integrations.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          This tenant has no integrations connected.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Integration</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Connected</TableHead>
                  <TableHead>Health</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((integration) => (
                  <TableRow
                    key={integration.id}
                    className={cn(!integration.enabled && "opacity-60")}
                  >
                    <TableCell className="max-w-[240px]">
                      <div className="truncate text-sm font-medium">
                        {integration.label || integration.name}
                      </div>
                      <div
                        className="text-muted-foreground truncate font-mono text-xs"
                        title={integration.id}
                      >
                        {integration.provider} · {integration.id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{integration.category}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {ago(integration.created_at)}
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <HealthCell
                        tenantId={tenantId}
                        integration={integration}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {pageCount > 1 && (
            <div className="flex items-center justify-end gap-2">
              <span className="text-muted-foreground text-xs">
                {page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, rows.length)} of {rows.length}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                aria-label="Previous page"
                disabled={page === 0}
                onClick={() => {
                  setPage(page - 1);
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                aria-label="Next page"
                disabled={page >= pageCount - 1}
                onClick={() => {
                  setPage(page + 1);
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </ToolCard>
  );
}
